/**
 * Página de éxito post-compra (rutas: `/muchas-gracias`, `/invitacion-premium/success`).
 *
 * Valida el pago vía `/api/validate-payment` (Stripe: `session_id` / `payment_intent`; metadata `plan_tier`).
 * Según el plan resuelto (**basic** | **standard** | **vip**): textos, estilos y enlace final distintos.
 *
 * **VIP (producción — checkout Stripe):** botón principal y **segundo botón** con otra URL.
 * - Principal: `VITE_SUCCESS_REDIRECT_VIP` → dashboard `success_redirect_url` → `VITE_SUCCESS_REDIRECT_VIP_AR`.
 * - Secundario (si la URL es distinta del principal): dashboard `success_secondary_*` o env
 *   `VITE_SUCCESS_REDIRECT_VIP_SECONDARY` + label opcional `VITE_SUCCESS_REDIRECT_VIP_SECONDARY_LABEL`.
 *
 * Basic/standard: dashboard → `telegram_url` en query → `VITE_SUCCESS_REDIRECT_STANDARD`.
 */

import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { AlertCircle, ArrowRight, CheckCircle, Crown, ExternalLink, Loader2, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { DATAFAST_GOALS, trackDataFastGoal } from "@/lib/datafastAnalytics";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

/**
 * Redirect URLs are now configured per brand via /dashboard/subscripciones/planes.
 * These fallbacks are used only when the API returns null (not yet configured).
 * For a new brand: set them via the dashboard → Planes de Suscripción → URL de éxito.
 */
const FALLBACK_TELEGRAM_URL = import.meta.env.VITE_SUCCESS_REDIRECT_STANDARD as string | undefined;
const FALLBACK_VIP_URL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP as string | undefined;
/** Post-pago VIP vía Mercado Pago (Argentina u otro retorno MP). Misma idea que `VITE_SUCCESS_REDIRECT_STANDARD` para basic. */
const FALLBACK_VIP_AR_URL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP_AR as string | undefined;
const FALLBACK_VIP_SECONDARY_URL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP_SECONDARY as string | undefined;
const FALLBACK_VIP_SECONDARY_LABEL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP_SECONDARY_LABEL as
  | string
  | undefined;

function trimEnvUrl(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t || undefined;
}

function isSafeCtaUrl(url: string): boolean {
  if (url.startsWith("/")) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Stripe vs Mercado Pago: `provider` en query o heurística por params típicos de MP. */
function resolvePaymentProvider(sp: URLSearchParams): "mercadopago" | "stripe" {
  const raw = sp.get("provider")?.toLowerCase().trim();
  if (raw === "mercadopago" || raw === "mp") return "mercadopago";
  if (
    sp.get("collection_id") ||
    sp.get("collection_status") ||
    sp.get("preference_id") ||
    sp.get("preapproval_id") ||
    sp.get("payment_id")
  ) {
    return "mercadopago";
  }
  return "stripe";
}

// ─── Labels por idioma ────────────────────────────────────────────────────────

interface SuccessLabels {
  // Plan basic — acceso directo al grupo privado de Telegram
  basicTitle: string;
  basicSubtitle: string;
  basicMessage: string;
  basicCta: string;
  // Plan standard — canal Telegram estándar
  title: string;
  subtitle: string;
  processedBy: string;
  standardMessage: string;
  standardCta: string;
  // Plan VIP — portal exclusivo
  vipThanksTitle: string;
  vipThanksSubtitle: string;
  vipMessage: string;
  vipCta: string;
  /** Segundo CTA VIP en /muchas-gracias (URL distinta). */
  vipSecondaryCta: string;
  vipFooterHint: string;
  // Estados
  validating: string;
  errorTitle: string;
  errorHelp: string;
}

const LABELS: Record<string, SuccessLabels> = {
  es: {
    basicTitle: "¡Acceso activado!",
    basicSubtitle: "Tu membresía Basic está lista",
    basicMessage: "Ya podés ingresar al grupo privado de Telegram. Hacé clic en el botón para unirte ahora.",
    basicCta: "Ingresar al Grupo Privado",
    title: "¡Pago exitoso!",
    subtitle: "Tu acceso está listo",
    processedBy: "Procesado por",
    standardMessage: `Bienvenido al canal exclusivo de ${BRAND_PERSON_NAME}. Hacé clic para unirte al grupo de Telegram.`,
    standardCta: "Ingresar al canal",
    vipThanksTitle: "¡Gracias de corazón! 🤍",
    vipThanksSubtitle: "Tu membresía VIP fue aprobada — te estaba esperando.",
    vipMessage:
      `Me alegra muchísimo que estés acá. Para activar tu acceso y recibir tu invitación privada de ${BRAND_PERSON_NAME}, creá tu cuenta ahora. En cuanto confirme tu registro, te habilito personalmente.`,
    vipCta: "Crear mi cuenta",
    vipSecondaryCta: "Acceso directo",
    vipFooterHint: "Es solo un momento — después te escribo con todo 🤍",
    validating: "Verificando tu pago...",
    errorTitle: "No pudimos verificar tu pago",
    errorHelp: "Si realizaste el pago correctamente, contactá al soporte con tu número de transacción.",
  },
  en: {
    basicTitle: "Access activated!",
    basicSubtitle: "Your Basic membership is ready",
    basicMessage: "You can now join the private Telegram group. Click the button below to enter.",
    basicCta: "Join Private Group",
    title: "Payment successful!",
    subtitle: "Your access is ready",
    processedBy: "Processed by",
    standardMessage: `Welcome to ${BRAND_PERSON_NAME}'s exclusive channel. Click to join the Telegram group.`,
    standardCta: "Join the channel",
    vipThanksTitle: "Thank you from the heart! 🤍",
    vipThanksSubtitle: "Your VIP membership is approved — I was waiting for you.",
    vipMessage:
      `I'm so glad you're here. To activate your access and receive your private invitation from ${BRAND_PERSON_NAME}, create your account now. As soon as I confirm your registration, I'll personally enable you.`,
    vipCta: "Create my account",
    vipSecondaryCta: "Direct access",
    vipFooterHint: "Just a moment — I'll reach out with everything 🤍",
    validating: "Verifying your payment...",
    errorTitle: "We could not verify your payment",
    errorHelp: "If you completed the payment, please contact support with your transaction number.",
  },
  pt: {
    basicTitle: "Acesso ativado!",
    basicSubtitle: "Sua assinatura Basic está pronta",
    basicMessage: "Agora você pode entrar no grupo privado do Telegram. Clique no botão abaixo para ingressar.",
    basicCta: "Entrar no Grupo Privado",
    title: "Pagamento aprovado!",
    subtitle: "Seu acesso está pronto",
    processedBy: "Processado por",
    standardMessage: `Bem-vindo ao canal exclusivo da ${BRAND_PERSON_NAME}. Clique para entrar no grupo do Telegram.`,
    standardCta: "Entrar no canal",
    vipThanksTitle: "Muito obrigado de coração! 🤍",
    vipThanksSubtitle: "Sua assinatura VIP foi aprovada — eu estava te esperando.",
    vipMessage:
      `Fico muito feliz que você esteja aqui. Para ativar seu acesso e receber seu convite privado de ${BRAND_PERSON_NAME}, crie sua conta agora. Assim que confirmar seu cadastro, eu habilito pessoalmente.`,
    vipCta: "Criar minha conta",
    vipSecondaryCta: "Acesso direto",
    vipFooterHint: "É só um momento — depois te escrevo com tudo 🤍",
    validating: "Verificando seu pagamento...",
    errorTitle: "Não conseguimos verificar seu pagamento",
    errorHelp: "Se você concluiu o pagamento, entre em contato com o suporte com seu número de transação.",
  },
  de: {
    basicTitle: "Zugang aktiviert!",
    basicSubtitle: "Deine Basic-Mitgliedschaft ist bereit",
    basicMessage: "Du kannst jetzt der privaten Telegram-Gruppe beitreten. Klick auf den Button, um beizutreten.",
    basicCta: "Private Gruppe beitreten",
    title: "Zahlung erfolgreich!",
    subtitle: "Dein Zugang ist bereit",
    processedBy: "Verarbeitet von",
    standardMessage: `Willkommen im exklusiven Kanal von ${BRAND_PERSON_NAME}. Klick, um der Telegram-Gruppe beizutreten.`,
    standardCta: "Kanal beitreten",
    vipThanksTitle: "Herzlichen Dank! 🤍",
    vipThanksSubtitle: "Deine VIP-Mitgliedschaft wurde bestätigt — ich habe auf dich gewartet.",
    vipMessage:
      `Ich freue mich sehr, dass du hier bist. Um deinen Zugang zu aktivieren und deine private Einladung von ${BRAND_PERSON_NAME} zu erhalten, erstelle jetzt dein Konto. Sobald ich deine Registrierung bestätige, schalte ich dich persönlich frei.`,
    vipCta: "Mein Konto erstellen",
    vipSecondaryCta: "Direktzugang",
    vipFooterHint: "Nur einen Moment — dann schreibe ich dir mit allem 🤍",
    validating: "Zahlung wird überprüft...",
    errorTitle: "Zahlung konnte nicht verifiziert werden",
    errorHelp: "Wenn du die Zahlung abgeschlossen hast, kontaktiere den Support mit deiner Transaktionsnummer.",
  },
  fr: {
    basicTitle: "Accès activé !",
    basicSubtitle: "Votre abonnement Basic est prêt",
    basicMessage: "Vous pouvez maintenant rejoindre le groupe privé Telegram. Cliquez sur le bouton pour y accéder.",
    basicCta: "Rejoindre le Groupe Privé",
    title: "Paiement réussi !",
    subtitle: "Votre accès est prêt",
    processedBy: "Traité par",
    standardMessage: `Bienvenue sur la chaîne exclusive de ${BRAND_PERSON_NAME}. Cliquez pour rejoindre le groupe Telegram.`,
    standardCta: "Rejoindre la chaîne",
    vipThanksTitle: "Merci du fond du cœur ! 🤍",
    vipThanksSubtitle: "Ton abonnement VIP a été approuvé — je t'attendais.",
    vipMessage:
      `Je suis si heureux(se) que tu sois là. Pour activer ton accès et recevoir ton invitation privée de ${BRAND_PERSON_NAME}, crée ton compte maintenant. Dès que je confirme ton inscription, je t'active personnellement.`,
    vipCta: "Créer mon compte",
    vipSecondaryCta: "Accès direct",
    vipFooterHint: "Juste un moment — je t'écris ensuite avec tout 🤍",
    validating: "Vérification du paiement...",
    errorTitle: "Nous n'avons pas pu vérifier votre paiement",
    errorHelp: "Si vous avez effectué le paiement, contactez le support avec votre numéro de transaction.",
  },
  it: {
    basicTitle: "Accesso attivato!",
    basicSubtitle: "Il tuo abbonamento Basic è pronto",
    basicMessage: "Ora puoi entrare nel gruppo privato Telegram. Clicca sul pulsante per accedere.",
    basicCta: "Entra nel Gruppo Privato",
    title: "Pagamento riuscito!",
    subtitle: "Il tuo accesso è pronto",
    processedBy: "Elaborato da",
    standardMessage: `Benvenuto nel canale esclusivo di ${BRAND_PERSON_NAME}. Clicca per unirti al gruppo Telegram.`,
    standardCta: "Entra nel canale",
    vipThanksTitle: "Grazie di cuore! 🤍",
    vipThanksSubtitle: "Il tuo abbonamento VIP è stato approvato — ti stavo aspettando.",
    vipMessage:
      `Sono davvero felice che tu sia qui. Per attivare il tuo accesso e ricevere il tuo invito privato da ${BRAND_PERSON_NAME}, crea il tuo account ora. Non appena confermo la tua registrazione, ti abilito personalmente.`,
    vipCta: "Crea il mio account",
    vipSecondaryCta: "Accesso diretto",
    vipFooterHint: "Solo un momento — poi ti scrivo con tutto 🤍",
    validating: "Verifica del pagamento in corso...",
    errorTitle: "Non siamo riusciti a verificare il tuo pagamento",
    errorHelp: "Se hai completato il pagamento, contatta il supporto con il tuo numero di transazione.",
  },
};

function getLabels(language: string): SuccessLabels {
  const base = language.split("-")[0].toLowerCase();
  return LABELS[base] ?? LABELS["en"];
}

type ValidationState = "loading" | "valid" | "error";

// ─── Componente ───────────────────────────────────────────────────────────────

export default function PremiumInviteSuccess() {
  useSEO();
  const [searchParams] = useSearchParams();
  const { language: workerLanguage } = useTemplateSelection();
  const capturePremiumEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const posthog = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture(event, { source: BRAND_SOURCE_KEY, ...props });
    }
  };

  const language        = searchParams.get("lang") || workerLanguage || "es";
  const provider        = resolvePaymentProvider(searchParams);
  const gatewayLabel    = provider === "mercadopago" ? "Mercado Pago" : "Stripe";
  const paymentIntentId = searchParams.get("payment_intent");
  const sessionId       = searchParams.get("session_id") || searchParams.get("checkout_session_id");
  const planFromUrl     = searchParams.get("plan") || searchParams.get("plan_tier");
  const flowTraceId     = searchParams.get("flow_trace_id") || undefined;
  /** URL del grupo de Telegram pasada por BioTemplatePremiumSimple al iniciar el checkout. */
  const telegramUrlFromParam = searchParams.get("telegram_url") || undefined;
  const mpSubscriptionId =
    searchParams.get("subscription_id") ||
    searchParams.get("preapproval_id") ||
    searchParams.get("preapprovalId") ||
    searchParams.get("preapproval_plan_id");
  /** Comunidad VIP AR: back_url del plan MP → success sin ids a veces. */
  const forceVipFromUrl =
    planFromUrl === "vip" ||
    searchParams.get("from_vip") === "1" ||
    Boolean(mpSubscriptionId && provider === "mercadopago");

  const L = getLabels(language);

  const [validationState, setValidationState] = useState<ValidationState>("loading");
  const [validatedPlan, setValidatedPlan]     = useState<"basic" | "standard" | "vip">(
    forceVipFromUrl || planFromUrl === "vip" ? "vip" : "basic",
  );
  const [redirectUrl, setRedirectUrl]         = useState<string | null>(null);
  const [redirectLabel, setRedirectLabel]     = useState<string | null>(null);
  const [secondaryRedirectUrl, setSecondaryRedirectUrl]   = useState<string | null>(null);
  const [secondaryRedirectLabel, setSecondaryRedirectLabel] = useState<string | null>(null);
  const [errorMessage, setErrorMessage]       = useState<string | null>(null);

  useEffect(() => {
    let effectiveSessionId: string | null = sessionId;
    if (!paymentIntentId && !effectiveSessionId) {
      try {
        const raw = localStorage.getItem("premium_last_stripe_session");
        if (raw) {
          const parsed = JSON.parse(raw) as {
            sessionId?: string;
            flowTraceId?: string;
            createdAt?: number;
          };
          const candidate = typeof parsed.sessionId === "string" ? parsed.sessionId.trim() : "";
          const ageMs =
            typeof parsed.createdAt === "number" ? Date.now() - parsed.createdAt : Number.POSITIVE_INFINITY;
          const traceMatches =
            !flowTraceId ||
            !parsed.flowTraceId ||
            String(parsed.flowTraceId).trim() === String(flowTraceId).trim();
          const freshEnough = ageMs < 2 * 60 * 60 * 1000;
          if (candidate && freshEnough && traceMatches) {
            effectiveSessionId = candidate;
          }
        }
      } catch {
        // ignore fallback parse errors
      }
    }

    // MP hosted VIP (comunidad / invitacion-premium): continuar a Clerk aunque falten ids.
    if (!paymentIntentId && !effectiveSessionId) {
      if (provider === "mercadopago" && (forceVipFromUrl || planFromUrl === "vip" || mpSubscriptionId)) {
        capturePremiumEvent("premium_success_mp_soft_continue", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          plan_tier: "vip",
          has_mp_subscription_id: Boolean(mpSubscriptionId),
        });
        setValidatedPlan("vip");
        setValidationState("valid");
        return;
      }
      capturePremiumEvent("premium_success_missing_identifier", {
        provider,
        language,
        flow_trace_id: flowTraceId,
        has_payment_intent: false,
        has_session_id: false,
      });
      setErrorMessage("No se encontró el identificador del pago en la URL (payment_intent/session_id).");
      setValidationState("error");
      return;
    }

    const validate = async () => {
      try {
        capturePremiumEvent("premium_success_validation_started", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          has_payment_intent: Boolean(paymentIntentId),
          has_session_id: Boolean(effectiveSessionId),
        });
        const traceParam = flowTraceId ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}` : "";
        const url = paymentIntentId
          ? `/api/validate-payment?payment_intent=${encodeURIComponent(paymentIntentId)}${traceParam}`
          : `/api/validate-payment?session_id=${encodeURIComponent(effectiveSessionId as string)}${traceParam}`;
        const res = await fetch(url);
        const data = await res.json() as { valid: boolean; plan?: string; error?: string };

        if (!res.ok || !data.valid) {
          capturePremiumEvent("premium_success_validation_failed", {
            provider,
            language,
            flow_trace_id: flowTraceId,
            status: res.status,
            error: data.error || "invalid_payment",
            payment_intent: paymentIntentId || undefined,
            session_id: effectiveSessionId || undefined,
          });
          setErrorMessage(data.error || "El pago no pudo ser verificado.");
          setValidationState("error");
          return;
        }

        // "basic" del URL param tiene prioridad porque validate-payment puede retornar
        // "standard" como fallback cuando el tier no se reconoce en la DB.
        // La API ahora sí devuelve "basic" si está en los metadata de Stripe, pero
        // el URL param es la fuente más confiable para este flow.
        const resolvedPlan = planFromUrl === "basic"
          ? "basic"
          : (data.plan || planFromUrl || "standard");
        capturePremiumEvent("premium_success_validated", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          plan_tier: resolvedPlan,
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        // DataFast: Stripe success page only (funnel — not revenue; Connect owns $)
        if (provider === "stripe") {
          trackDataFastGoal(DATAFAST_GOALS.checkoutSuccessPage, {
            page: "premium_invite_success",
            plan_tier: resolvedPlan,
            revenue_type: "mrr",
            language,
          });
        }
        setValidatedPlan(
          resolvedPlan === "vip"
            ? "vip"
            : resolvedPlan === "standard"
              ? "standard"
              : "basic"
        );
        setValidationState("valid");
      } catch {
        capturePremiumEvent("premium_success_validation_failed", {
          provider,
          language,
          flow_trace_id: flowTraceId,
          error: "network_error",
          payment_intent: paymentIntentId || undefined,
          session_id: effectiveSessionId || undefined,
        });
        setErrorMessage("Error de conexión al verificar el pago.");
        setValidationState("error");
      }
    };

    validate();
  }, [paymentIntentId, sessionId, planFromUrl, flowTraceId, provider, language]);

  // ── Fetch redirect URL for the validated plan tier ────────────────────────
  // Called once the payment is validated and we know the plan tier.
  useEffect(() => {
    if (validationState !== "valid") return;
    const fetchRedirectUrl = async () => {
      try {
        const res = await fetch(`/api/membership/success-config?plan_tier=${encodeURIComponent(validatedPlan)}`);
        if (!res.ok) return;
        const data = await res.json() as {
          success_redirect_url?: string | null;
          success_redirect_label?: string | null;
          success_secondary_redirect_url?: string | null;
          success_secondary_redirect_label?: string | null;
        };
        if (data.success_redirect_url) setRedirectUrl(data.success_redirect_url);
        if (data.success_redirect_label) setRedirectLabel(data.success_redirect_label);
        if (data.success_secondary_redirect_url) setSecondaryRedirectUrl(data.success_secondary_redirect_url);
        if (data.success_secondary_redirect_label) setSecondaryRedirectLabel(data.success_secondary_redirect_label);
      } catch {
        // Non-fatal — falls back to env vars / hardcoded defaults below
      }
    };
    void fetchRedirectUrl();
  }, [validationState, validatedPlan]);

  // ── Derivados visuales ────────────────────────────────────────────────────
  const isVip      = validatedPlan === "vip";
  const isBasic    = validatedPlan === "basic";
  // basic → sky/Telegram blue  |  standard → green  |  vip → amber/gold
  const gradient = isVip
    ? "from-amber-400 via-orange-500 to-yellow-500"
    : isBasic
      ? "from-sky-400 via-blue-500 to-cyan-500"
      : "from-green-500 via-emerald-500 to-teal-500";
  const glowColor = isVip
    ? "bg-amber-500/40"
    : isBasic
      ? "bg-sky-500/40"
      : "bg-green-500/40";
  const btnGradient = isVip
    ? "from-amber-500 to-orange-500"
    : isBasic
      ? "from-sky-500 to-blue-600"
      : "from-green-500 to-emerald-500";

  const envVipAr = trimEnvUrl(FALLBACK_VIP_AR_URL);
  const envVipIntl = trimEnvUrl(FALLBACK_VIP_URL);
  /** VIP post–Stripe Checkout: portal internacional primero, luego dashboard, luego env AR. */
  const vipPrimaryEnv = envVipIntl ?? envVipAr;

  // Resolve the redirect URL:
  // VIP: `VITE_SUCCESS_REDIRECT_VIP` → dashboard → `VITE_SUCCESS_REDIRECT_VIP_AR`; nunca telegram_url en query.
  // Basic/standard: dashboard → telegram_url → VITE_SUCCESS_REDIRECT_STANDARD.
  const primaryCtaUrl = isVip
    ? (envVipIntl ?? redirectUrl ?? envVipAr ?? null)
    : (redirectUrl ?? (telegramUrlFromParam ?? trimEnvUrl(FALLBACK_TELEGRAM_URL) ?? null) ?? null);

  const secondaryCandidate = isVip
    ? (trimEnvUrl(secondaryRedirectUrl) ?? trimEnvUrl(FALLBACK_VIP_SECONDARY_URL) ?? null)
    : null;
  const secondaryCtaUrl =
    secondaryCandidate &&
    isSafeCtaUrl(secondaryCandidate) &&
    secondaryCandidate !== primaryCtaUrl
      ? secondaryCandidate
      : null;
  const secondaryCtaLabel =
    isVip && secondaryCtaUrl
      ? (trimEnvUrl(secondaryRedirectLabel) ?? trimEnvUrl(FALLBACK_VIP_SECONDARY_LABEL) ?? L.vipSecondaryCta)
      : null;

  /** VIP → Clerk sign-up → completar perfil → pending (comunidad / invitacion-premium). */
  const registroUrl = useMemo<string>(() => {
    const parts: string[] = ["plan_tier=vip", "from_vip=1"];
    if (provider === "mercadopago") {
      parts.push("from_mp=true");
      const identifier =
        searchParams.get("subscription_id") ||
        searchParams.get("preapproval_id") ||
        searchParams.get("payment_id") ||
        searchParams.get("collection_id") ||
        mpSubscriptionId;
      if (identifier) parts.push(`subscription_id=${encodeURIComponent(identifier)}`);
    } else {
      if (sessionId) parts.push(`session_id=${encodeURIComponent(sessionId)}`);
      if (paymentIntentId) parts.push(`payment_intent=${encodeURIComponent(paymentIntentId)}`);
    }
    if (flowTraceId) parts.push(`flow_trace_id=${encodeURIComponent(flowTraceId)}`);
    return `${MEMBERSHIP_ROUTE.PORTAL_REGISTRO}?${parts.join("&")}`;
  }, [provider, searchParams, sessionId, paymentIntentId, flowTraceId, mpSubscriptionId]);

  // Auto-redirect (membresía VIP): tras validar el pago, llevamos SOLO (sin click)
  // al flujo registro → completar perfil. Delay corto para que se vea la confirmación
  // y disparen las analytics; la CTA queda como fallback manual.
  const navigate = useNavigate();
  const [autoRedirected, setAutoRedirected] = useState(false);
  useEffect(() => {
    if (validationState !== "valid" || !isVip || autoRedirected) return;
    const t = setTimeout(() => {
      setAutoRedirected(true);
      capturePremiumEvent("premium_success_vip_auto_redirect", {
        provider,
        language,
        flow_trace_id: flowTraceId,
        plan_tier: validatedPlan,
        destination_url: registroUrl,
        payment_intent: paymentIntentId || undefined,
        session_id: sessionId || undefined,
      });
      navigate(registroUrl);
    }, 3500);
    return () => clearTimeout(t);
  }, [
    validationState, isVip, autoRedirected, registroUrl, navigate,
    provider, language, flowTraceId, validatedPlan, paymentIntentId, sessionId,
  ]);

  const pageTitle    = isVip ? L.vipThanksTitle    : isBasic ? L.basicTitle    : L.title;
  const pageSubtitle = isVip ? L.vipThanksSubtitle : isBasic ? L.basicSubtitle : L.subtitle;
  const message      = isVip ? L.vipMessage        : isBasic ? L.basicMessage  : L.standardMessage;
  // VIP: si el enlace sale del bloque env intl+AR (no del dashboard), usar copy i18n del portal.
  const ctaLabel =
    isVip && vipPrimaryEnv && primaryCtaUrl === vipPrimaryEnv
      ? L.vipCta
      : redirectLabel ?? (isVip ? L.vipCta : isBasic ? L.basicCta : L.standardCta);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (validationState === "loading") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black" />
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">{L.validating}</p>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (validationState === "error") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-pink-500 to-rose-500 opacity-15" />
        </div>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="relative inline-flex justify-center w-full">
            <div className="absolute inset-0 blur-2xl bg-red-500/30 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center shadow-2xl">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-red-400">{L.errorTitle}</h1>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-red-500/20">
            <p className="text-gray-300 text-sm leading-relaxed mb-2">{errorMessage}</p>
            <p className="text-gray-500 text-xs">{L.errorHelp}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Éxito ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-black text-white flex items-center justify-center px-4 select-none"
      style={{ fontFamily: FONT_FAMILY, WebkitUserSelect: "none" } as React.CSSProperties}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      <div className="fixed inset-0 -z-10 bg-black">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative inline-flex justify-center w-full">
          <div className={`absolute inset-0 blur-2xl ${glowColor} animate-pulse`} />
          <div className={`relative w-24 h-24 bg-gradient-to-br ${gradient} rounded-full flex items-center justify-center shadow-2xl`}>
            {isVip
              ? <Crown className="w-12 h-12 text-white" />
              : <CheckCircle className="w-12 h-12 text-white" />
            }
          </div>
        </div>

        <div>
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
            {pageTitle}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {pageSubtitle}
          </p>
        </div>

        <div
          className={`rounded-2xl p-6 border backdrop-blur-sm select-none ${
            isVip
              ? "border-amber-500/30 bg-amber-500/5"
              : isBasic
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-white/10 bg-white/5"
          }`}
          onContextMenu={(e) => e.preventDefault()}
        >
          <p className="text-gray-300 text-base leading-relaxed mb-4">{message}</p>

          {/* "Procesado por" solo para VIP y standard — basic es más directo */}
          {!isBasic && (
            <p className="text-gray-500 text-xs mb-6">
              {L.processedBy} {gatewayLabel}
            </p>
          )}

          {/* VIP: flujo de registro → /registro → /pending-approval → admin aprueba y envía link privado */}
          {isVip ? (
            <div className="w-full space-y-3">
              <a
                href={registroUrl}
                className={`w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 ui-transition duration-200 flex items-center justify-center gap-2 select-none`}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() =>
                  capturePremiumEvent("premium_success_vip_registro_click", {
                    provider,
                    language,
                    flow_trace_id: flowTraceId,
                    plan_tier: validatedPlan,
                    destination_url: registroUrl,
                    payment_intent: paymentIntentId || undefined,
                    session_id: sessionId || undefined,
                  })
                }
              >
                <UserPlus className="w-5 h-5" />
                <span>{L.vipCta}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              {!autoRedirected && (
                <p className="text-gray-500 text-xs text-center flex items-center justify-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Te llevamos a completar tu perfil…
                </p>
              )}
              {/* Portal directo (opcional) — si el admin configuró un dashboard redirect */}
              {(primaryCtaUrl ?? secondaryCtaUrl) ? (
                <a
                  href={primaryCtaUrl ?? secondaryCtaUrl ?? ""}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 px-6 rounded-xl font-semibold text-amber-50 border border-amber-400/45 bg-amber-500/10 hover:bg-amber-500/15 active:scale-[0.98] ui-transition duration-200 flex items-center justify-center gap-2 select-none"
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() =>
                    capturePremiumEvent("premium_success_vip_portal_click", {
                      provider,
                      language,
                      flow_trace_id: flowTraceId,
                      plan_tier: validatedPlan,
                      destination_url: primaryCtaUrl ?? secondaryCtaUrl,
                      payment_intent: paymentIntentId || undefined,
                      session_id: sessionId || undefined,
                    })
                  }
                >
                  <ExternalLink className="w-5 h-5 shrink-0 opacity-90" />
                  <span>{secondaryCtaLabel ?? L.vipSecondaryCta}</span>
                </a>
              ) : null}
            </div>
          ) : primaryCtaUrl ? (
            /* basic / standard: Telegram directo */
            <a
              href={primaryCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 ui-transition duration-200 flex items-center justify-center gap-2 select-none`}
              onContextMenu={(e) => e.preventDefault()}
              onClick={() =>
                capturePremiumEvent(
                  isBasic ? "premium_success_basic_telegram_click" : "premium_success_telegram_click",
                  {
                    provider,
                    language,
                    flow_trace_id: flowTraceId,
                    plan_tier: validatedPlan,
                    destination_url: primaryCtaUrl,
                    payment_intent: paymentIntentId || undefined,
                    session_id: sessionId || undefined,
                  },
                )
              }
            >
              {/* Telegram icon */}
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              <span>{ctaLabel}</span>
            </a>
          ) : (
            /* No redirect URL configurada para este brand */
            <div className="w-full py-4 px-6 rounded-xl border border-white/10 text-gray-500 text-sm text-center">
              Tu membresía está activa. El acceso será habilitado en breve — te contactaremos por email.
            </div>
          )}
          {isVip && (
            <p className="text-gray-500 text-xs mt-4 text-center">{L.vipFooterHint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
